import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {DateTime} from 'luxon';
import PageMeta from 'components/PageMeta';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faLock} from '@fortawesome/pro-light-svg-icons';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState, useUserDispatch} from 'contexts/user';
import ShowIf from 'funnel-schedule/ShowIf';
import track from 'utils/track';

import Webinar from 'components/Webinar';
import TntgCountdownSection from 'components/TntgCountdownSection';
import Divider from 'components/Divider';
import SalesCopy from 'components/SalesCopy';
import {SalesCta67} from 'components/SalesCta';
import CheckoutButton from 'components/CheckoutButton';
import ModalExitScreening from 'components/ModalExitScreening';
import RegistrationModal from 'components/RegistrationModal';
import FollowButtons from 'components/FollowButtons';
import ShareButtons from 'components/ShareButtons';
import Footer from 'components/Footer';
import WebinarVideo from 'components/WebinarVideo';

//Due to a limitation of dotenv we must use three seperate statements
const {SF_API_URL} = process.env;
const {SITE_ID} = process.env;

export default function Screening({screeningFull = false, ...props}) {
  const phaseDispatch = usePhaseDispatch();
  const userDispatch = useUserDispatch();
  const {phases = {}} = usePhaseState();
  const {default: context = {}} = phases;
  const {phase} = context;
  const {events = [], registrationId} = useUserState();
  const videoWatched =
    events.includes('Watched TNTG Screening') || (typeof window !== 'undefined' && window.location.hash === '#impact');
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  useEffect(() => {
    registrationId &&
      axios
        .get(`${SF_API_URL}/site/${SITE_ID}/state/`, {
          params: {
            registrationId
          }
        })
        .then(({data}) => {
          if (!data.webinar) {
            data.webinar = {
              phase: 'after',
              time: Math.floor(DateTime.utc().minus({days: 5}).toSeconds())
            };
          }
          const {firedEvents = []} = data.webinar;

          phaseDispatch({
            type: 'SET_PHASE',
            payload: data
          });
          firedEvents.map((e) => userDispatch({type: 'ADD_EVENT', payload: e}));
        });
  }, []);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes('Watched TNTG Screening')) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#impact') {
        document.getElementById('impact-cta').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  useEffect(
    () =>
      track('Product Viewed', {
        product_id: 'tntg',
        sku: 'tntg',
        name: 'TNTG Impact Kit',
        variant: 'tntg-impactkit-special-wlc-30daytrial-st',
        value: 67,
        currency: 'usd'
      }),
    []
  );
  useEffect(
    () =>
      track('Product Viewed', {
        product_id: 'tntg',
        sku: 'tntg',
        name: 'TNTG Film',
        variant: 'tntg-special',
        value: 7,
        currency: 'usd'
      }),
    []
  );

  const delayedReveal = videoWatched;

  return (
    <div className="screening-page page d-flex flex-column">
      <PageMeta />

      <div className="page-content">
        <div className="background-gradient-yellow-white">
          <ShowIf phase="before" context="webinar">
            <div className="background-light-blue triangle-bottom triangle-bottom-light-blue wrapper">
              <div className="offset-header">
                <Container>
                  <Row>
                    <Col>
                      <h3 className="text-center text-white mb-0">
                        Thank you for saving your spot <br className="d-none d-md-block d-lg-none" />
                        for the <br className="d-none d-lg-block" />
                        online screening of <br className="d-none d-sm-block d-lg-none" />
                        <em>The Need To GROW.</em>
                      </h3>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
            <div className="video-box offset-video-box">
              <Container>
                <Row>
                  <Col className="px-0 px-sm-3">
                    <div className="video-box-prelaunch text-center background-black px-4 py-5 px-lg-5 text-white">
                      <Row className="d-flex justify-content-center">
                        <Col xs="12" md="10" lg="12">
                          <h4 className="text-condensed text-800">
                            Please look for an email from <br className="d-none d-sm-block d-lg-none" />
                            us with more <br className="d-none d-lg-block" />
                            information about <br className="d-none d-sm-block d-lg-none" />
                            the online screening.
                          </h4>
                        </Col>
                      </Row>
                      <Row className="d-flex justify-content-center mb-4">
                        <Col xs="12">
                          <h5 className="text-400 mb-0">
                            With all grassroots movements, success <br className="d-none d-md-block d-lg-none" />
                            depends on your support. <br className="d-none d-xl-block" />
                            Please use the share buttons on this page to tell your friends and family{' '}
                            <br className="d-none d-xl-block" />
                            about this upcoming exclusive free online screening event! We look{' '}
                            <br className="d-none d-xl-block" />
                            forward to sharing <br className="d-none d-md-block d-xl-none" />
                            this special film with you!
                          </h5>
                        </Col>
                      </Row>
                      <Row className="text-center">
                        <Col>
                          <div className="mb-3">
                            <ShareButtons alignment="justify-content-center" shareText />
                          </div>
                          <FollowButtons alignment="justify-content-center" followText />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          </ShowIf>

          <ShowIf phase={['replay']} context="webinar">
            <div className="background-black triangle-bottom triangle-bottom-black wrapper">
              <div className="offset-header-during">
                <Container>
                  <Row>
                    <Col>
                      <h3 className="text-center text-800 text-white mb-2">
                        Welcome to the exclusive online <br className="d-none d-lg-block" />
                        screening of <em>The Need To GROW.</em>
                      </h3>
                      <h5 className="text-center text-800 text-white mb-0">
                        An environmental film to <br className="d-none d-sm-block d-md-none" />
                        give the world hope.
                      </h5>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
            <div className="video-box offset-video-box-during pb-5">
              <Container>
                <Row>
                  <Col className="px-0 px-sm-3">
                    <Webinar noun="Free Screening" />
                  </Col>
                </Row>
              </Container>
            </div>
            <Container>
              <TntgCountdownSection />
            </Container>
          </ShowIf>

          <ShowIf phase="after" context="webinar">
            <div className="background-light-blue triangle-bottom triangle-bottom-light-blue wrapper">
              <div className="offset-header">
                <Container>
                  <Row>
                    <Col>
                      <h3 className="text-center text-white mb-0">
                        Your limited time free <br className="d-none d-sm-block d-md-none" />
                        viewing period of <br className="d-none d-md-block" />
                        <em>
                          The Need <br className="d-none d-sm-block d-md-none" />
                          To GROW
                        </em>{' '}
                        has ended.
                      </h3>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
            <div className="video-box offset-video-box">
              <Container>
                <Row>
                  <Col className="px-0 px-sm-3">
                    <div className="video-box-prelaunch text-center background-black px-4 py-5 px-lg-5 text-white">
                      <Row className="d-flex justify-content-center mb-4">
                        <Col xs="12" lg="10" xl="8">
                          <h5 className="text-400 mb-0">
                            With all grassroots movements, success depends on your support. Please use the share buttons
                            on this page to tell your friends and family about this exclusive free online screening
                            event!
                          </h5>
                        </Col>
                      </Row>
                      <Row className="text-center">
                        <Col>
                          <div className="mb-3">
                            <ShareButtons alignment="justify-content-center" shareText />
                          </div>
                          <FollowButtons alignment="justify-content-center" followText />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          </ShowIf>
        </div>

        <Divider className="mt-5" />

        <div className={classNames(`takehome wrapper`, delayedReveal ? 'triangle-angles-offset-bottom' : 'pb-5')}>
          <Container>
            <Row className="d-flex align-items-center justify-content-center">
              <Col xs="8" sm="6" lg="4" className="text-center mb-4 mb-lg-0">
                <img src="https://cdn.foodrevolution.org/tntg/social/tntg-iv-square.jpg" />
              </Col>
              <Col xs="12" md="10" lg="8" xl="7" className="text-center text-lg-left">
                <h4 className="text-condensed text-800 mb-3">
                  Feeling inspired and <br className="d-none d-md-block d-xl-none" />
                  want to <em>stay</em> that way?
                </h4>
                <h4 className="text-condensed text-800">
                  Own the movie for life and help <br className="d-none d-sm-block" />
                  spread this urgent mission now.
                </h4>
                <h5 className="mb-2">
                  <strong>Right now just $7!</strong>
                </h5>
                <p className="mb-4">(Less than the cost of a movie ticket!)</p>
                <CheckoutButton className="btn-size-350" sku="tntg" price="7" slug="tntg-special" />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="offer-box-details text-center mt-4">
                  <div className="offer-box-cards">
                    <img
                      className="img-fluid img-cc"
                      src="https://cdn.foodrevolution.org/global/images/payments-accepted-20240424.svg"
                      width={1800}
                      height={175}
                    />
                  </div>
                  <div className="offer-box-security">
                    <FontAwesomeIcon className="offer-box-security-icon mr-2" icon={faLock} />
                    For your security, all orders are processed on a secured server.
                  </div>
                  <div className="offer-box-protected d-flex justify-content-center">
                    Your purchase is protected by our 60-day money back guarantee.
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {delayedReveal && (
          <div className="impact-delay" id="impact-delay">
            <SalesCopy
              screeningTestimonials
              salesCta={
                <>
                  <SalesCta67 trees />
                </>
              }
            />
          </div>
        )}
      </div>

      <div className="mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>
      <ModalExitScreening />
      <RegistrationModal />
    </div>
  );
}
